// src/app/data/payments/api/PaymentsApi.ts
import { DataFetcher } from '@/app/data/api/DataFetcher';

export interface TokenBalance {
  userId: number;
  currentTokens: number;
  updatedAt: string;
}

export interface UserMeResponse {
  userId: number;
  isActive: boolean;
  referralCode: string;
  language: string;
  defaultModel: string;
  defaultImageModel?: string;
  defaultVideoModel?: string;
  lastUsedConversationId: number | null;
  isNovice: boolean;
  createdAt: string;
  mediaConfirm?: boolean;
}

export interface TokenPackagePrice {
  currency: 'RUB' | 'XTR';
  amountMinor: number;
}

export interface TokenPackageFromCatalog {
  code: string;
  title: string;
  tokenAmount: number;
  prices: TokenPackagePrice[];
}

export interface CheckoutPreviewRequest {
  packageCode: string;
  currency: 'RUB' | 'XTR';
  promoCode?: string;
}

export interface CheckoutPreviewResponse {
  packageCode: string;
  currency: string;
  baseAmountMinor: number;
  finalAmountMinor: number;
  applied?: {
    source: 'PROMO_CODE' | 'REFERRAL';
    discountMinor: number;
    promoCode?: string;
  };
  bonuses?: Array<{
    source: string;
    type: string;
    tokenAmount: number;
  }>;
  skipped?: Record<string, string>;
}

export interface CreatePaymentRequest {
  packageCode: string;
  currency: 'RUB' | 'XTR';
  providerCode: string;
  promoCode?: string;
}

export interface PaymentResponse {
  paymentId: number;
  status: string;
  amountMinor: number;
  currency: string;
  invoiceUrl?: string;
  invoicePayload?: string;
  applied?: {
    source: string;
    discountMinor: number;
    promoCode?: string;
  };
  bonuses?: Array<{
    source: string;
    type: string;
    tokenAmount: number;
  }>;
}

export interface PromoRedeemResponse {
  promoCode: string;
  tokenAmount: number;
  redemptionId: number;
  alreadyRedeemed: boolean;
  currentTokens: number;
}

export class PaymentsApi {
  private fetcher = new DataFetcher();

  async getMe(): Promise<UserMeResponse> {
    return this.fetcher.get<UserMeResponse>('/client/me');
  }

  async getBalance(): Promise<TokenBalance> {
    return this.fetcher.get<TokenBalance>('/client/tokens/balance');
  }

  async getTokenPackages(currency?: 'RUB' | 'XTR'): Promise<TokenPackageFromCatalog[]> {
    const params = currency ? { currency } : undefined;
    return this.fetcher.get<TokenPackageFromCatalog[]>('/client/catalog/token-packages', params);
  }

  async previewCheckout(payload: CheckoutPreviewRequest): Promise<CheckoutPreviewResponse> {
    return this.fetcher.post<CheckoutPreviewResponse>('/client/checkout/preview', payload);
  }

   async createPayment(payload: CreatePaymentRequest): Promise<PaymentResponse> {
    const idempotencyKey = this.generateIdempotencyKey();
    return this.fetcher.post<PaymentResponse>(
      '/client/payments',
      payload,
      undefined,                         // query-параметры не нужны
      { 'Idempotency-Key': idempotencyKey }   // ← заголовок
    );
  }

  async getPaymentStatus(paymentId: number): Promise<PaymentResponse> {
    return this.fetcher.get<PaymentResponse>(`/client/payments/${paymentId}`);
  }

  /** Полинг статуса платежа */
  async pollPaymentStatus(
    paymentId: number,
    options?: { maxAttempts?: number; delayMs?: number },
  ): Promise<string> {
    const maxAttempts = options?.maxAttempts ?? 15;
    const delayMs = options?.delayMs ?? 2500;

    for (let attempt = 0; attempt < maxAttempts; attempt++) {
      const status = await this.getPaymentStatus(paymentId);
      if (
        status.status === 'PAID' ||
        status.status === 'FAILED' ||
        status.status === 'CANCELLED' ||
        status.status === 'EXPIRED'
      ) {
        return status.status;
      }
      await new Promise(resolve => setTimeout(resolve, delayMs));
    }
    return 'FAILED';
  }

  /** Сообщение об ошибке по статусу */
  getCheckoutErrorMessage(status: string): string {
    switch (status) {
      case 'CANCELLED':
        return 'Платеж отменен. Попробуйте еще раз.';
      case 'EXPIRED':
        return 'Время оплаты истекло. Запустите платеж заново.';
      case 'FAILED':
        return 'Платеж не удался. Повторите попытку позже.';
      default:
        return 'Не удалось завершить оплату. Повторите попытку позже.';
    }
  }

  async redeemPromo(code: string, idempotencyKey?: string): Promise<PromoRedeemResponse> {
  const key = idempotencyKey || this.generateIdempotencyKey();
  return this.fetcher.post<PromoRedeemResponse>(
    '/client/promotions/redeem',          // правильный endpoint
    { code },
    undefined,                            // query-параметры не нужны
    { 'Idempotency-Key': key }            // заголовок четвёртым аргументом
  );
}

  private generateIdempotencyKey(): string {
    return `ik-${Date.now()}-${Math.random().toString(36).substring(2, 10)}`;
  }
}